import {
  IconButton,
  ButtonGroup,
  ButtonProps,
  Portal,
  PortalManager,
  Action,
  Actions,
  FlagProps,
  Flags,
} from 'platform/components';
import {Box, Heading, HStack, VStack, Scroll, Show, ThemeColorPath} from 'platform/foundation';
import {useTheme} from 'styled-components';

import {ReactNode, useEffect} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useFocusControl} from '../FocusControlProvider/FocusControlProvider';

export interface FullScreenModalProps extends TestIdProps {
  headline?: string;
  children?: ReactNode;
  actions?: ButtonProps[];
  customActions?: ReactNode | (() => ReactNode);
  headerActions?: Action[];
  subtitleActions?: Action[];
  onClose?: VoidFunction;
  backgroundColor?: ThemeColorPath;
  scrollable?: boolean;
  flags?: FlagProps[];
}

export function FullScreenModal(props: FullScreenModalProps) {
  const theme = useTheme();
  const {disable, enable} = useFocusControl();

  const testId = props['data-testid'] ? suffixTestId('wrapper', props) : 'fullScreenModal';

  useEffect(() => {
    disable();
    return () => {
      enable();
    };
  }, [disable, enable]);

  return (
    <PortalManager zIndex={theme.zIndices.FULLSCREEN_MODAL}>
      <Portal>
        <Box
          position="fixed"
          left={0}
          right={0}
          top={0}
          bottom={0}
          height="100%"
          width="100%"
          backgroundColor={props.backgroundColor ?? 'palettes.neutral.20.100'}
          data-testid={testId}
        >
          <VStack height="100%">
            <Box
              padding={3}
              borderBottom="1px solid"
              borderColor="general.separator"
              backgroundColor="palettes.white.10.100"
              data-testid={suffixTestId('header', props)}
            >
              <VStack spacing={1}>
                <HStack justify="space-between" align="center">
                  <HStack spacing={2}>
                    {props.headline ? <Heading size={3}>{props.headline}</Heading> : null}

                    <Show when={isNotNilOrEmpty(props.flags)}>
                      <Flags flags={props.flags} />
                    </Show>
                  </HStack>

                  <Show when={isNotNilOrEmpty(props.headerActions)}>
                    <Actions
                      actions={props.headerActions}
                      data-testid={suffixTestId('headerActions', props)}
                    />
                  </Show>

                  <Show when={isNotNilOrEmpty(props.actions)}>
                    <ButtonGroup
                      data-testid={suffixTestId('actions', props)}
                      buttons={props.actions}
                    />
                  </Show>

                  <Show when={props.customActions}>
                    {typeof props.customActions === 'function'
                      ? props.customActions()
                      : props.customActions}
                  </Show>

                  {props.onClose ? (
                    <IconButton
                      priority="secondary"
                      onClick={props.onClose}
                      icon="navigation/close"
                      data-testid={suffixTestId('close-button', props)}
                    />
                  ) : null}
                </HStack>

                <Show when={isNotNilOrEmpty(props.subtitleActions)}>
                  <Actions
                    actions={props.subtitleActions}
                    data-testid={suffixTestId('subtitleActions', props)}
                  />
                </Show>
              </VStack>
            </Box>

            <Scroll auto width="100%" height="100%" data-testid={suffixTestId('content', props)}>
              {props.children}
            </Scroll>
          </VStack>
        </Box>
      </Portal>
    </PortalManager>
  );
}
